import { CircularProgress, TextField } from '@mui/material'
import { RefObject } from 'react'
import { useTranslation } from 'react-i18next'

import { DefectAssistantSuggestion, OptionValue } from '../../../actions/defectAssistant'
import { DefectCodeAdviceEntry } from '../../../reducers/createDefectAssistantSlice'
import { AiButton } from '../CtaButtons'
import { optionKey } from './shared'
import {
  AI_FIELD_SX,
  FinishSection,
  OptionDesc,
  OptionsList,
  OptionState,
  OptionTitle,
  ScrollOptionButton,
  Section,
  SectionQuestion,
  SectionReasoning,
} from './styles'

// A suggestions list. The "Ei mikään näistä" / meta extras stay in place in every block; they are
// only interactive on the live active block before it has been used.
export const SuggestionsBlock = ({
  sugs,
  activeBlock,
  showHeader,
  suggestionPicked,
  selectedCode,
  metaOptions,
  additionalDetailsVisible,
  analyzeLoading,
  defectCodeAdviceByCode,
  onPick,
  onMeta,
  onExclude,
}: {
  sugs: DefectAssistantSuggestion[]
  activeBlock: boolean
  showHeader: boolean
  suggestionPicked: boolean
  selectedCode: string
  metaOptions: OptionValue[]
  additionalDetailsVisible: boolean
  analyzeLoading: boolean
  // This block's own per-prefix "why to choose" defectCodeAdvice (streamed in separately, keyed by the round's
  // generationId upstream). Locked history rounds pass their own generation's map too, so defectCodeAdvice that
  // was still loading when the round was discarded keeps filling in. Only falls back to the inline
  // s.description when no map is available (e.g. a round with no generationId).
  defectCodeAdviceByCode?: Record<string, DefectCodeAdviceEntry>
  onPick: (s: DefectAssistantSuggestion) => void
  onMeta: (opt: OptionValue) => void
  onExclude: (codes: string[]) => void
}): JSX.Element => {
  const { t } = useTranslation()
  const extrasInteractive =
    activeBlock && !suggestionPicked && !additionalDetailsVisible && !analyzeLoading

  // Renders one suggestion's defectCodeAdvice line: a spinner until its block has streamed in, the defectCodeAdvice
  // when ready, or the localized fallback when it timed out / produced nothing.
  const renderDefectCodeAdvice = (
    s: DefectAssistantSuggestion,
    dimmed: boolean
  ): JSX.Element | null => {
    if (!defectCodeAdviceByCode) {
      return s.description ? <OptionDesc dimmed={dimmed}>{s.description}</OptionDesc> : null
    }
    const status = defectCodeAdviceByCode[s.defectCode]?.status ?? 'loading'
    if (status === 'ready') {
      return <OptionDesc dimmed={dimmed}>{defectCodeAdviceByCode[s.defectCode].text}</OptionDesc>
    }
    if (status === 'failed') {
      return (
        <OptionDesc dimmed={dimmed}>{t('defect.aiAssistant.defectCodeAdviceFailed')}</OptionDesc>
      )
    }
    return (
      <OptionDesc dimmed={dimmed}>
        <CircularProgress size={14} />
      </OptionDesc>
    )
  }
  return (
    <Section>
      {/* Only the first block carries the "Valitse vikakoodi" prompt; later blocks are just more
          options for that same prompt, so they render headerless. */}
      {showHeader && (
        <SectionQuestion muted={!activeBlock || suggestionPicked}>
          {t('defect.aiAssistant.suggestionsHeader')}
        </SectionQuestion>
      )}
      <OptionsList>
        {sugs.map((s, idx) => {
          const label = s.category
            ? `${s.category} ➜ ${s.label || s.defectCode}`
            : s.label || s.defectCode
          // Suggestions stay choosable in every round (even earlier ones, even while a new analysis
          // runs) until a pick is made; then the picked one is highlighted and the rest dim.
          const optionState: OptionState = suggestionPicked
            ? s.defectCode === selectedCode
              ? 'selected'
              : 'secondary'
            : 'active'
          return (
            <ScrollOptionButton
              key={`${s.defectCode}-${idx}`}
              type="button"
              optionState={optionState}
              onClick={() => (optionState === 'active' || optionState === 'secondary') && onPick(s)}
            >
              <OptionTitle dimmed={optionState === 'secondary'}>{label}</OptionTitle>
              {renderDefectCodeAdvice(s, optionState === 'secondary')}
            </ScrollOptionButton>
          )
        })}
        {extrasInteractive && metaOptions.length > 0
          ? metaOptions.map((opt, idx) => (
              <ScrollOptionButton
                key={`meta-${optionKey(opt)}-${idx}`}
                type="button"
                optionState="active"
                onClick={() => onMeta(opt)}
              >
                <OptionTitle>{opt.label ?? ''}</OptionTitle>
                {opt.additionalInfo && <OptionDesc>{opt.additionalInfo}</OptionDesc>}
              </ScrollOptionButton>
            ))
          : sugs.length > 0 && (
              <ScrollOptionButton
                type="button"
                // Stays in place in every round; only clickable on the live block before use.
                optionState={extrasInteractive ? 'active' : 'dimmed'}
                onClick={() => extrasInteractive && onExclude(sugs.map((s) => s.defectCode))}
              >
                <OptionTitle dimmed={!extrasInteractive}>
                  {t('defect.aiAssistant.noneOfTheseTitle')}
                </OptionTitle>
                <OptionDesc dimmed={!extrasInteractive}>
                  {t('defect.aiAssistant.noneOfTheseReasoning')}
                </OptionDesc>
              </ScrollOptionButton>
            )}
      </OptionsList>
    </Section>
  )
}

// The "Kerro lisää viasta" interlude. The same elements (label, help, field, button) are always
// present in all three modes — only enabled/disabled and the button content change, so nothing ever
// appears or disappears.
export const Interlude = ({
  mode,
  detail,
  value,
  onChange,
  onSubmit,
}: {
  mode: 'input' | 'loading' | 'locked'
  detail: string
  value: string
  onChange: (v: string) => void
  onSubmit: () => void
}): JSX.Element => {
  const { t } = useTranslation()
  return (
    <Section>
      <SectionQuestion muted={mode !== 'input'}>
        {t('defect.aiAssistant.additionalDetailsTitle')}
      </SectionQuestion>
      <SectionReasoning>{t('defect.aiAssistant.additionalDetailsHelp')}</SectionReasoning>
      <TextField
        multiline
        minRows={3}
        fullWidth
        disabled={mode !== 'input'}
        value={mode === 'input' ? value : detail}
        onChange={mode === 'input' ? (e) => onChange(e.target.value) : undefined}
        placeholder={t('defect.aiAssistant.additionalDetailsPlaceholder')}
        sx={AI_FIELD_SX}
      />
      {mode === 'loading' ? (
        <AiButton type="button" disabled>
          <CircularProgress size={20} sx={{ color: 'white' }} />
          {t('defect.aiAssistant.analyzing')}
        </AiButton>
      ) : (
        <AiButton
          type="button"
          disabled={mode === 'locked'}
          onClick={mode === 'input' ? onSubmit : undefined}
        >
          {t('defect.aiAssistant.retryAnalyze')}
        </AiButton>
      )}
    </Section>
  )
}

// The terminal "go to review" step — enabled only when the flow is complete.
export const FinishStep = ({
  flowComplete,
  amendLoading,
  activeRef,
  onFinish,
}: {
  flowComplete: boolean
  amendLoading: boolean
  activeRef: RefObject<HTMLDivElement>
  onFinish: () => void
}): JSX.Element => {
  const { t } = useTranslation()
  if (flowComplete) {
    return (
      <div ref={activeRef}>
        <FinishSection>
          <AiButton type="button" disabled={amendLoading} onClick={onFinish}>
            {t('defect.aiAssistant.goToReview')}
          </AiButton>
        </FinishSection>
      </div>
    )
  }
  return (
    <FinishSection style={{ opacity: 0.25 }}>
      <AiButton type="button" disabled>
        {t('defect.aiAssistant.goToReview')}
      </AiButton>
    </FinishSection>
  )
}
